import { useLightMode } from '@/hooks/useLightMode'
import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import { useTranslation } from 'react-i18next'

import { Alert, Box, Modal } from '@hivewatch/dark'

import { useDeleteStandardOperatingProcedureMutation } from '@/graphql/generated/hooks'
import { tVar } from '@/utils/i18n'

interface RemoveSOPModalIProps {
  isOpen: boolean
  onClose: () => void
  sop: {
    id: string
    name: string
  }
  refetch: () => void
}

export const RemoveSOPModal = ({
  isOpen,
  onClose,
  sop,
  refetch,
}: RemoveSOPModalIProps) => {
  const { t } = useTranslation()
  const colorScheme = useLightMode()
  const { shouldEnableRemoveModal } = usePermissions()
  const { showSuccess, showError } = useToasts()

  const [deleteSOP, { loading: isDeleteSOPLoading }] =
    useDeleteStandardOperatingProcedureMutation()

  const onSubmit = async () => {
    try {
      await deleteSOP({
        variables: {
          input: {
            id: sop?.id,
          },
        },
      })
      refetch?.()
      showSuccess(tVar(t, 'sops:crud_remove_success', { name: sop.name }))
      onClose()
    } catch {
      showError()
    }
  }

  return (
    <Modal
      colorScheme={colorScheme}
      isOpen={isOpen}
      isSaveDestructive
      isSaveDisabled={isDeleteSOPLoading || !shouldEnableRemoveModal}
      isSaveLoading={isDeleteSOPLoading}
      onClose={onClose}
      onSave={onSubmit}
      saveButtonLabel={t('common:delete')}
      showCancelButton
      size='compact'
      title={t('sops:crud_remove_sop')}
    >
      <Box data-testid='RemoveSOPModal:question'>
        {tVar(t, 'sops:crud_remove_confirmation', { name: sop.name })}
      </Box>
      <Box mt='16px'>
        <Alert
          colorScheme={colorScheme}
          description={t('common:warning_cannot_be_undone')}
          title={t('common:warning_title')}
          variant='warning'
        />
      </Box>
    </Modal>
  )
}
